import type { ScheduleRow } from '../types/schedule';

/**
 * CSV export for the Positions tab.
 *
 * Input: the SAME `ScheduleRow[]` slice the Positions table renders
 * (filtered by Search). Output: a UTF-8 CSV string ready to hand to
 * `downloadFile`.
 *
 * Why ScheduleRow, not PositionRow:
 *   ScheduleRow is the raw wire shape from `/execution-schedule?format=positions`.
 *   It carries `broker`, `exchange`, `average_entry_price`, `average_exit_price`,
 *   etc. that the table-bound PositionRow drops or renames. Reconciliation
 *   against a broker statement (the #1 export use-case) needs those fields,
 *   so we export straight from the source rather than reverse-engineering
 *   them from PositionRow.
 *
 * Headers are stable snake_case; values are CSV-escaped per RFC 4180 (fields
 * containing comma / double-quote / newline are wrapped in `"…"`, internal
 * `"` is doubled to `""`). The output is prefixed with a UTF-8 BOM so Excel
 * opens it in the user's locale without garbling non-ASCII strategy names.
 */

/**
 * Column manifest — order is load-bearing because it's what the user sees
 * after opening the file. Adding a column means appending here (and to the
 * matching `cell()` switch); removing one means deleting both sides.
 *
 * Kept tight at ~30 columns: enough to reconcile, not so wide the file
 * scrolls horizontally past readability in Excel/Sheets.
 */
const COLUMNS = [
  'execution_date',
  'client_id',
  'broker',
  'basket_name',
  'strategy_name',
  'underlying',
  'symbol',
  'exchange',
  'product',
  'trading_type',
  'intraday_exit_mode',
  'action',
  'lots',
  'quantity',
  'position_status',
  'entry_status',
  'exit_status',
  'sl_status',
  'expected_entry_price',
  'average_entry_price',
  'entry_filled_at',
  'sl_trigger_price',
  'sl_limit_price',
  'sl_average_price',
  'sl_placed_at',
  'expected_exit_price',
  'exit_average_price',
  'exit_placed_at',
  'ltp',
  'pnl',
  'closed_date',
] as const;

type Column = typeof COLUMNS[number];

/** UTF-8 BOM — opt-in Excel locale support. Excel for Windows reads CSV
 *  as Windows-1252 by default; the BOM forces UTF-8 so option symbols
 *  with extended characters or non-ASCII basket names render correctly. */
const UTF8_BOM = '﻿';

/**
 * Format a numeric value for CSV. Money columns keep 2dp; integer counts
 * emit no decimals; null/undefined become empty cells (NOT the string
 * "null", which would parse as text in Sheets).
 */
function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  if (!Number.isFinite(v)) return '';
  return v.toFixed(2);
}

function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  if (!Number.isFinite(v)) return '';
  return String(Math.trunc(v));
}

function fmtStr(v: string | null | undefined): string {
  if (v === null || v === undefined) return '';
  return v;
}

/** Per-column extractor. Centralised so the column list above is the only
 *  ordering authority — adding a column = one entry here. */
function cell(col: Column, r: ScheduleRow): string {
  switch (col) {
    case 'execution_date':       return fmtStr(r.execution_date);
    case 'client_id':            return fmtStr(r.client_id);
    case 'broker':               return fmtStr(r.broker);
    case 'basket_name':          return fmtStr(r.basket_name);
    case 'strategy_name':        return fmtStr(r.strategy_name);
    case 'underlying':           return fmtStr(r.underlying);
    case 'symbol':               return fmtStr(r.symbol);
    case 'exchange':             return fmtStr(r.exchange);
    case 'product':              return fmtStr(r.product);
    case 'trading_type':         return fmtStr(r.trading_type);
    case 'intraday_exit_mode':   return fmtStr(r.intraday_exit_mode);
    case 'action':               return fmtStr(r.action);
    case 'lots':                 return fmtInt(r.lots ?? null);
    case 'quantity':             return fmtInt(r.quantity);
    case 'position_status':      return fmtStr(r.position_status);
    case 'entry_status':         return fmtStr(r.entry_status);
    case 'exit_status':          return fmtStr(r.exit_status);
    case 'sl_status':            return fmtStr(r.sl_status);
    case 'expected_entry_price': return fmtMoney(r.expected_entry_price);
    case 'average_entry_price':  return fmtMoney(r.average_entry_price);
    case 'entry_filled_at':      return fmtStr(r.entry_filled_at);
    case 'sl_trigger_price':     return fmtMoney(r.sl_trigger_price);
    case 'sl_limit_price':       return fmtMoney(r.sl_limit_price);
    case 'sl_average_price':     return fmtMoney(r.sl_average_price);
    case 'sl_placed_at':         return fmtStr(r.sl_placed_at);
    case 'expected_exit_price':  return fmtMoney(r.expected_exit_price);
    case 'exit_average_price':   return fmtMoney(r.exit_average_price);
    case 'exit_placed_at':       return fmtStr(r.exit_placed_at);
    case 'ltp':                  return fmtMoney(r.ltp);
    case 'pnl':                  return fmtMoney(r.pnl);
    case 'closed_date':          return fmtStr(r.closed_date);
  }
}

/**
 * RFC 4180 field escaping. Wrap in `"…"` if the field contains comma,
 * double-quote, CR, or LF; double any internal `"`.
 */
function escapeField(raw: string): string {
  if (raw === '') return '';
  const needsQuoting = /[",\r\n]/.test(raw);
  if (!needsQuoting) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
}

/**
 * Build the CSV string. Always emits the header row even when `rows` is
 * empty — keeps the downloaded file's schema stable so any spreadsheet
 * template the user has set up keeps working across days.
 */
export function toPositionsCsv(rows: ScheduleRow[]): string {
  const header = COLUMNS.map(escapeField).join(',');
  const body = rows.map((r) => COLUMNS.map((c) => escapeField(cell(c, r))).join(','));
  return UTF8_BOM + [header, ...body].join('\n') + '\n';
}

/**
 * Filename helper — `positions-{date}-{HHmmss}.csv`. The schedule date
 * tells the user which trading day the snapshot is for; the timestamp
 * lets them download multiple snapshots in one session without overwrite.
 */
export function positionsCsvFilename(selectedDate: string, now: Date = new Date()): string {
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `positions-${selectedDate}-${hh}${mm}${ss}.csv`;
}

/** Exposed for tests — not part of the public surface used by the page. */
export const __test__ = { COLUMNS, escapeField, fmtMoney, fmtInt, UTF8_BOM };
